/** Normalize DB language to UI locale; unknown values default to English. */
export const normalizeEventLanguage = (value) => {
  const v = String(value ?? '').toLowerCase().trim();
  if (v === 'es') return 'es';
  if (v === 'en') return 'en';
  return 'en';
};

/**
 * Inline copy for registration (no i18n library).
 * All user-visible strings for this flow should come from here.
 */
export const REGISTRATION_COPY = {
  en: {
    loadingEvent: 'Loading event...',
    loadingRegistration: 'Loading your registration...',
    eventNotFoundTitle: 'Event Not Found',
    eventNotFoundDescription: "The event you're trying to register for doesn't exist or isn't available.",
    pageTitle: 'Complete Your Registration',
    pageSubtitlePrefix: "You're just one step away from joining:",
    stepOf: (step) => `Step ${step} of 3`,
    pctComplete: (pct) => `${pct}% Complete`,
    personalInformation: 'Personal Information',
    labelFullName: 'Full Name *',
    phFullName: 'Enter your full name',
    errFullName: 'Full name is required',
    labelEmail: 'Email Address *',
    phEmail: 'Enter your email',
    errEmailRequired: 'Email is required',
    errEmailInvalid: 'Invalid email address',
    labelPhone: 'Phone Number',
    phPhone: 'Enter your phone number',
    labelInvitedBy: 'Who invited you?',
    phOptional: 'Optional',
    errInvitedByMax: 'Invited by must be 100 characters or less',
    btnContinue: 'Continue',
    btnProcessing: 'Processing...',
    orientationLaterTitle: "You're all set for the Free Orientation!",
    orientationLaterBody:
      "We'll send you an email with a link to complete your full registration whenever you're ready. You can also return to this page and use the link from that email to pick up where you left off.",
    orientationLaterInboxPrefix: 'Check your inbox at',
    orientationLaterInboxSuffix: 'for next steps.',
    orientationRegisteredTitle: "You've been registered for the Free Orientation session!",
    orientationRegisteredBody:
      "You can attend the orientation without completing payment or creating an account. When you're ready, complete your full registration to access all sessions and materials.",
    btnContinuePremium: 'Continue to Premium Registration',
    btnCompleteLater: "I'll Complete Later",
    paymentInformation: 'Payment Information',
    paymentConfirmed: 'Payment confirmed. Proceeding to registration.',
    paymentContinuePrompt: 'Complete your payment to continue to the final step.',
    confirmRegistration: 'Confirm Registration',
    eventSummary: 'Event Summary',
    summaryEvent: 'Event:',
    summaryDate: 'Date:',
    summaryTime: 'Time:',
    summaryPrice: 'Price:',
    priceFree: 'FREE',
    btnBack: 'Back',
    btnConfirming: 'Confirming...',
    registeredSuccessTitle: "You're registered!",
    registeredSuccessBodyPrefix: 'Your spot is secured for',
    registeredSuccessBodySuffix: 'Check your email for confirmation and next steps.',
    registeredSuccessFooter: 'You can create an account later to access your dashboard and join the event.',
    linkReturnHome: 'Return home',
    accountCreateTitle: "You're registered! Create your account (optional)",
    accountCreateSubtitle:
      'Your event spot is secured. Create a password to sign in and access your student dashboard, or skip and use the link we sent to your email.',
    labelPassword: 'Password *',
    phPassword: 'Create a password',
    errPasswordRequired: 'Password is required',
    errPasswordMin: 'Password must be at least 6 characters',
    labelConfirmPassword: 'Confirm Password *',
    phConfirmPassword: 'Re-enter your password',
    errConfirmPassword: 'Please confirm your password',
    errPasswordMismatch: 'Passwords do not match',
    eventSummaryShort: 'Event summary',
    accountNoteRegistered:
      "You're already registered for this event. This step only creates your account for dashboard access.",
    whatHappensNext: 'What happens next:',
    nextBullet1: "You'll receive a confirmation email immediately",
    nextBullet2: 'Zoom link will be sent 24 hours before the event',
    nextBullet3: 'Recording will be available after the event',
    nextBullet4: 'All resources will be accessible in your dashboard',
    skipForNow: 'Skip for now',
    submitCreateAccount: 'Create account & go to dashboard',
    toastRegistrationSuccess: 'Registration successful! Welcome to the event.',
    toastRegistrationFailed: 'Registration failed. Please try again.',
    toastCaptureFailed: 'Unable to save your information. Please try again.',
    toastGenericError: 'Something went wrong. Please try again.',
    toastConfirmFailed: 'Could not confirm registration.',
    toastSkipSuccess: 'Registration complete! Check your email for details.',
    errPaymentRequired: 'Please complete payment before finishing registration.',
    errUnableCreateAccount: 'Unable to create your account. Please check your email for next steps.',
    errRegistrationComplete: 'Unable to complete registration.',
  },
  es: {
    loadingEvent: 'Cargando evento...',
    loadingRegistration: 'Cargando tu registro...',
    eventNotFoundTitle: 'Evento no encontrado',
    eventNotFoundDescription:
      'El evento al que intentas registrarte no existe o no está disponible.',
    pageTitle: 'Completa tu registro',
    pageSubtitlePrefix: 'Estás a un paso de unirte a:',
    stepOf: (step) => `Paso ${step} de 3`,
    pctComplete: (pct) => `${pct}% completado`,
    personalInformation: 'Información personal',
    labelFullName: 'Nombre completo *',
    phFullName: 'Ingresa tu nombre completo',
    errFullName: 'El nombre completo es obligatorio',
    labelEmail: 'Correo electrónico *',
    phEmail: 'Ingresa tu correo',
    errEmailRequired: 'El correo es obligatorio',
    errEmailInvalid: 'Correo electrónico no válido',
    labelPhone: 'Teléfono',
    phPhone: 'Ingresa tu número de teléfono',
    labelInvitedBy: '¿Quién te invitó?',
    phOptional: 'Opcional',
    errInvitedByMax: '«Quién te invitó» debe tener 100 caracteres o menos',
    btnContinue: 'Continuar',
    btnProcessing: 'Procesando...',
    orientationLaterTitle: '¡Todo listo para la orientación gratuita!',
    orientationLaterBody:
      'Te enviaremos un correo con un enlace para completar tu registro completo cuando estés listo. También puedes volver a esta página y usar el enlace de ese correo para continuar donde lo dejaste.',
    orientationLaterInboxPrefix: 'Revisa tu bandeja en',
    orientationLaterInboxSuffix: 'para los siguientes pasos.',
    orientationRegisteredTitle: '¡Te has registrado para la sesión de orientación gratuita!',
    orientationRegisteredBody:
      'Puedes asistir a la orientación sin completar el pago ni crear una cuenta. Cuando estés listo, completa tu registro completo para acceder a todas las sesiones y materiales.',
    btnContinuePremium: 'Continuar al registro premium',
    btnCompleteLater: 'Completaré más tarde',
    paymentInformation: 'Información de pago',
    paymentConfirmed: 'Pago confirmado. Continuando con el registro.',
    paymentContinuePrompt: 'Completa el pago para pasar al último paso.',
    confirmRegistration: 'Confirmar registro',
    eventSummary: 'Resumen del evento',
    summaryEvent: 'Evento:',
    summaryDate: 'Fecha:',
    summaryTime: 'Hora:',
    summaryPrice: 'Precio:',
    priceFree: 'GRATIS',
    btnBack: 'Atrás',
    btnConfirming: 'Confirmando...',
    registeredSuccessTitle: '¡Estás registrado!',
    registeredSuccessBodyPrefix: 'Tu lugar está reservado para',
    registeredSuccessBodySuffix: 'Revisa tu correo para la confirmación y los siguientes pasos.',
    registeredSuccessFooter:
      'Puedes crear una cuenta más tarde para acceder a tu panel y unirte al evento.',
    linkReturnHome: 'Volver al inicio',
    accountCreateTitle: '¡Estás registrado! Crea tu cuenta (opcional)',
    accountCreateSubtitle:
      'Tu lugar en el evento está asegurado. Crea una contraseña para iniciar sesión y acceder a tu panel de estudiante, u omite este paso y usa el enlace que enviamos a tu correo.',
    labelPassword: 'Contraseña *',
    phPassword: 'Crea una contraseña',
    errPasswordRequired: 'La contraseña es obligatoria',
    errPasswordMin: 'La contraseña debe tener al menos 6 caracteres',
    labelConfirmPassword: 'Confirmar contraseña *',
    phConfirmPassword: 'Vuelve a ingresar tu contraseña',
    errConfirmPassword: 'Por favor confirma tu contraseña',
    errPasswordMismatch: 'Las contraseñas no coinciden',
    eventSummaryShort: 'Resumen del evento',
    accountNoteRegistered:
      'Ya estás registrado en este evento. Este paso solo crea tu cuenta para acceder al panel.',
    whatHappensNext: 'Qué sigue:',
    nextBullet1: 'Recibirás un correo de confirmación de inmediato',
    nextBullet2: 'El enlace de Zoom se enviará 24 horas antes del evento',
    nextBullet3: 'La grabación estará disponible después del evento',
    nextBullet4: 'Todos los recursos estarán en tu panel',
    skipForNow: 'Omitir por ahora',
    submitCreateAccount: 'Crear cuenta e ir al panel',
    toastRegistrationSuccess: '¡Registro exitoso! Bienvenido al evento.',
    toastRegistrationFailed: 'El registro falló. Inténtalo de nuevo.',
    toastCaptureFailed: 'No se pudo guardar tu información. Inténtalo de nuevo.',
    toastGenericError: 'Algo salió mal. Inténtalo de nuevo.',
    toastConfirmFailed: 'No se pudo confirmar el registro.',
    toastSkipSuccess: '¡Registro completo! Revisa tu correo para más detalles.',
    errPaymentRequired: 'Completa el pago antes de finalizar el registro.',
    errUnableCreateAccount: 'No se pudo crear tu cuenta. Revisa tu correo para los siguientes pasos.',
    errRegistrationComplete: 'No se pudo completar el registro.',
  },
};

export const mapEventRecord = (row) => ({
  id: row.id,
  title: row.title ?? '',
  description: row.description ?? '',
  date: row.date ?? '',
  time: row.start_time ?? row.time ?? '',
  duration: row.duration ?? 0,
  price: row.price ?? 0,
  agentId: row.agent_id ?? null,
  use_platform_account: row.use_platform_account ?? false,
  has_free_orientation: row.has_free_orientation ?? false,
  orientation_session_id: row.orientation_session_id ?? null,
  language: normalizeEventLanguage(row?.language),
});
